import React, { useState } from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { getCityFromHost } from '../utils/getCityFromHost';

const FAQ = () => {
  const city = getCityFromHost();
  const cityName = city ? city.charAt(0).toUpperCase() + city.slice(1) : 'Your City';

  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: 'General',
      questions: [
        {
          q: 'What is Digital Fatehabad?',
          a: 'Digital Fatehabad is an online platform that helps you discover and connect with local businesses in your area. We provide comprehensive business listings, reviews, ratings, and contact information to help you find the services you need.'
        },
        {
          q: 'How do I search for businesses?',
          a: 'You can search for businesses by category, subcategory, or tags. Use the search bar on the homepage or browse through different categories. You can also filter results by location, ratings, and other criteria.'
        },
        {
          q: 'Is the service free to use?',
          a: 'Yes, browsing and searching for businesses is completely free for users. Businesses may choose to list their services on our platform, and we offer various listing options.'
        }
      ]
    },
    {
      category: 'For Businesses',
      questions: [
        {
          q: 'How do I list my business?',
          a: 'To list your business, click on "Submit Business" in the navigation menu, fill out the business information form, and submit it for review. Once approved by our team, your business will be visible on the directory.'
        },
        {
          q: 'How long does it take for my business to be approved?',
          a: 'Typically, business listings are reviewed and approved within 1-3 business days. You will receive an email notification once your listing is approved or if any additional information is needed.'
        },
        {
          q: 'Can I edit my business listing?',
          a: 'Yes, if you have a user account and the business is linked to your account, you can edit your business information at any time. Simply log in and go to your profile to manage your listings.'
        },
        {
          q: 'What information do I need to provide?',
          a: 'You will need to provide your business name, category, address, phone number, email, website (if applicable), and a brief description. Additional information like hours of operation and photos can enhance your listing.'
        }
      ]
    },
    {
      category: 'Reviews & Ratings',
      questions: [
        {
          q: 'How do I leave a review?',
          a: 'To leave a review, you need to create a free account. Once logged in, navigate to the business page and click on "Write a Review". You can rate the business and write a detailed review about your experience.'
        },
        {
          q: 'Can I edit or delete my review?',
          a: 'Yes, you can edit or delete your own reviews. Go to the business page where you left the review, and you will see options to edit or delete it if you are the author.'
        },
        {
          q: 'Are reviews moderated?',
          a: 'Yes, all reviews are subject to moderation to ensure they meet our community guidelines. Reviews that contain inappropriate content, spam, or violate our terms of service may be removed.'
        }
      ]
    },
    {
      category: 'Account & Profile',
      questions: [
        {
          q: 'How do I create an account?',
          a: 'Click on "Sign Up" in the navigation menu, fill in your name, email, phone number, and create a password. You will receive a confirmation email to verify your account.'
        },
        {
          q: 'I forgot my password. How do I reset it?',
          a: 'On the login page, click "Forgot Password" and enter your email address. You will receive instructions to reset your password via email.'
        },
        {
          q: 'Can I change my account information?',
          a: 'Yes, you can update your account information at any time by logging in and going to your profile page. Click "Edit Profile" to modify your details.'
        }
      ]
    },
    {
      category: 'Technical Support',
      questions: [
        {
          q: 'The website is not loading properly. What should I do?',
          a: 'Try clearing your browser cache, updating your browser to the latest version, or using a different browser. If the problem persists, please contact our support team.'
        },
        {
          q: 'How do I report a problem with a business listing?',
          a: 'If you notice incorrect information or have concerns about a business listing, please contact us through our Contact Us page with the details, and we will investigate.'
        },
        {
          q: 'Is the website mobile-friendly?',
          a: 'Yes, our website is fully responsive and optimized for mobile devices. You can access all features from your smartphone or tablet.'
        }
      ]
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <SEO
        title="Frequently Asked Questions"
        description="Find answers to FAQs about Digital Fatehabad: listings, reviews, accounts, how to submit a business, and more."
        canonical="/faq"
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-gray-600">
            Find answers to common questions about our platform
          </p>
        </div>

        <div className="space-y-8">
          {faqs.map((category, categoryIndex) => (
            <div key={categoryIndex} className="card p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{category.category}</h2>
              <div className="space-y-4">
                {category.questions.map((faq, index) => {
                  const globalIndex = categoryIndex * 100 + index;
                  const isOpen = openIndex === globalIndex;
                  
                  return (
                    <div key={index} className="border-b border-gray-200 last:border-0 pb-4 last:pb-0">
                      <button
                        onClick={() => toggleFAQ(globalIndex)}
                        className="w-full text-left flex items-center justify-between py-3 focus:outline-none group"
                      >
                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors pr-8">
                          {faq.q}
                        </h3>
                        <svg
                          className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {isOpen && (
                        <div className="mt-2 pl-0">
                          <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Still Have Questions */}
        <div className="card p-6 md:p-8 mt-8 bg-gradient-to-br from-blue-50 to-blue-100 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Still Have Questions?</h2>
          <p className="text-gray-700 mb-6">
            Can't find the answer you're looking for? Please feel free to contact our friendly team.
          </p>
          <Link
            to="/contact"
            className="inline-block px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </>
  );
};

export default FAQ;

